import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit, Trash2, Save, X, Layers, Upload, Download, FileSpreadsheet, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IndustryTaxonomyManager = () => {
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [editingTaxonomy, setEditingTaxonomy] = useState(null);
  const [formData, setFormData] = useState({
    primary_industry: '',
    secondary_industries: [],
    sub_industries: {},
    business_models: []
  });
  const [newSecondary, setNewSecondary] = useState('');
  const [newSubIndustry, setNewSubIndustry] = useState({ parent: '', value: '' });
  const [newBusinessModel, setNewBusinessModel] = useState('');
  
  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  const token = localStorage.getItem('token');

  // Fetch taxonomies
  const { data: taxonomies = [], isLoading } = useQuery({
    queryKey: ['industry-taxonomies'],
    queryFn: async () => {
      const response = await axios.get(`${API}/governance/industry-taxonomies`);
      return response.data;
    }
  });
  
  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ file, replaceAll }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API}/governance/industry-taxonomies/upload?replace_all=${replaceAll}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setUploadResult(data);
      toast.success(`${data.created} taxonomías creadas, ${data.updated} actualizadas`);
      queryClient.invalidateQueries(['industry-taxonomies']);
      setSelectedFile(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al cargar archivo');
    }
  });

  // Create/Update mutation
  const saveTaxonomyMutation = useMutation({
    mutationFn: async (data) => {
      if (editingTaxonomy) {
        const response = await axios.put(
          `${API}/governance/industry-taxonomy/${editingTaxonomy.id}`,
          data
        );
        return response.data;
      } else {
        const response = await axios.post(`${API}/governance/industry-taxonomy`, data);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['industry-taxonomies']);
      toast.success(editingTaxonomy ? 'Taxonomía actualizada' : 'Taxonomía creada');
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  });

  // Delete mutation
  const deleteTaxonomyMutation = useMutation({
    mutationFn: async (id) => {
      await axios.delete(`${API}/governance/industry-taxonomy/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['industry-taxonomies']);
      toast.success('Taxonomía eliminada');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  });

  const handleOpenDialog = (taxonomy = null) => {
    if (taxonomy) {
      setEditingTaxonomy(taxonomy);
      setFormData({
        primary_industry: taxonomy.primary_industry,
        secondary_industries: taxonomy.secondary_industries || [],
        sub_industries: taxonomy.sub_industries || {},
        business_models: taxonomy.business_models || []
      });
    } else {
      setEditingTaxonomy(null);
      setFormData({
        primary_industry: '',
        secondary_industries: [],
        sub_industries: {},
        business_models: []
      });
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingTaxonomy(null);
    setNewSecondary('');
    setNewSubIndustry({ parent: '', value: '' });
    setNewBusinessModel('');
  };

  const handleAddSecondary = () => {
    if (!newSecondary.trim()) return;
    
    setFormData({
      ...formData,
      secondary_industries: [...formData.secondary_industries, newSecondary.trim()]
    });
    setNewSecondary('');
  };

  const handleRemoveSecondary = (index) => {
    const updated = formData.secondary_industries.filter((_, i) => i !== index);
    // Also remove sub-industries for this secondary
    const updatedSubs = { ...formData.sub_industries };
    delete updatedSubs[formData.secondary_industries[index]];
    
    setFormData({
      ...formData,
      secondary_industries: updated,
      sub_industries: updatedSubs
    });
  };

  const handleAddSubIndustry = () => {
    if (!newSubIndustry.parent || !newSubIndustry.value.trim()) return;
    
    const updatedSubs = { ...formData.sub_industries };
    if (!updatedSubs[newSubIndustry.parent]) {
      updatedSubs[newSubIndustry.parent] = [];
    }
    updatedSubs[newSubIndustry.parent].push(newSubIndustry.value.trim());
    
    setFormData({ ...formData, sub_industries: updatedSubs });
    setNewSubIndustry({ parent: '', value: '' });
  };

  const handleRemoveSubIndustry = (parent, index) => {
    const updatedSubs = { ...formData.sub_industries };
    updatedSubs[parent] = updatedSubs[parent].filter((_, i) => i !== index);
    if (updatedSubs[parent].length === 0) {
      delete updatedSubs[parent];
    }
    setFormData({ ...formData, sub_industries: updatedSubs });
  };

  const handleAddBusinessModel = () => {
    if (!newBusinessModel.trim()) return;
    
    setFormData({
      ...formData,
      business_models: [...formData.business_models, newBusinessModel.trim()]
    });
    setNewBusinessModel('');
  };

  const handleRemoveBusinessModel = (index) => {
    setFormData({
      ...formData,
      business_models: formData.business_models.filter((_, i) => i !== index)
    });
  };

  const handleSave = () => {
    if (!formData.primary_industry.trim()) {
      toast.error('La industria principal es obligatoria');
      return;
    }

    saveTaxonomyMutation.mutate(formData);
  };

  const handleDelete = (taxonomy) => {
    if (window.confirm(`¿Eliminar la taxonomía "${taxonomy.primary_industry}"?`)) {
      deleteTaxonomyMutation.mutate(taxonomy.id);
    }
  };
  
  // File upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadResult(null);
    }
  };
  
  const handleUpload = () => {
    if (selectedFile) {
      if (replaceAll) {
        if (!window.confirm('¿Estás seguro? Esto eliminará TODAS las taxonomías existentes y cargará las nuevas.')) {
          return;
        }
      }
      uploadMutation.mutate({ file: selectedFile, replaceAll });
    }
  };
  
  const downloadTemplate = () => {
    const csvContent = `Industria,Modelo_Negocio,Estructura_Operativa,Categoria
Retail,B2C,Tienda Física,Ventas
Retail,B2C,Tienda Física,Logística
Retail,B2B,Distribución,Ventas
Retail,B2B,Distribución,Logística
Tecnología,SaaS,Cloud,Marketing
Tecnología,SaaS,Cloud,Soporte
Tecnología,Consultoría,Servicios,Proyectos
Manufactura,Producción,Planta,Producción
Manufactura,Producción,Planta,Calidad
Servicios,Financieros,Banca,Créditos
Servicios,Financieros,Banca,Inversiones`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla_taxonomias.csv';
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Taxonomía de Industrias</h2>
          <p className="text-slate-400">
            Define la estructura jerárquica de industrias para la clasificación automática
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="btn-gradient">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Industria
        </Button>
      </div>
      
      {/* Upload Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
              <Upload className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-white text-lg">Carga Masiva de Taxonomías</CardTitle>
              <CardDescription className="text-slate-400">
                Sube un archivo Excel o CSV con la estructura de industrias
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={downloadTemplate}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar Plantilla
            </Button>
            <div className="text-sm text-slate-500">
              Columnas: Industria, Modelo_Negocio, Estructura_Operativa, Categoria
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="bg-slate-900 border-slate-600 text-white file:bg-slate-700 file:text-slate-200 file:border-0"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="replaceAllTax"
                checked={replaceAll}
                onChange={(e) => setReplaceAll(e.target.checked)}
                className="rounded border-slate-600 bg-slate-900 text-purple-500 focus:ring-purple-500"
              />
              <Label htmlFor="replaceAllTax" className="text-slate-400 text-sm">
                Reemplazar todas
              </Label>
            </div>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploadMutation.isPending}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Cargar
                </>
              )}
            </Button>
          </div>
          
          {uploadResult && (
            <div className={`p-4 rounded-lg ${uploadResult.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-white">
                    {uploadResult.created} industrias creadas, {uploadResult.updated} actualizadas
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    Total de industrias procesadas: {uploadResult.total_industries}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Taxonomies List */}
      <div className="grid gap-4">
        {isLoading ? (
          <Card className="glass-dark border-slate-700">
            <CardContent className="p-8 text-center text-slate-400">
              Cargando taxonomías...
            </CardContent>
          </Card>
        ) : taxonomies.length === 0 ? (
          <Card className="glass-dark border-slate-700">
            <CardContent className="p-8 text-center">
              <Layers className="w-12 h-12 mx-auto mb-4 text-slate-500" />
              <p className="text-slate-400">No hay taxonomías definidas</p>
              <Button onClick={() => handleOpenDialog()} className="mt-4">
                Crear Primera Taxonomía
              </Button>
            </CardContent>
          </Card>
        ) : (
          taxonomies.map((taxonomy) => (
            <Card key={taxonomy.id} className="glass-dark border-blue-500/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Layers className="w-5 h-5 text-blue-400" />
                    {taxonomy.primary_industry}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleOpenDialog(taxonomy)}
                      variant="ghost"
                      size="sm"
                      className="text-blue-400"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(taxonomy)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Secondary Industries */}
                {taxonomy.secondary_industries && taxonomy.secondary_industries.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Industrias Secundarias:</p>
                    <div className="flex flex-wrap gap-2">
                      {taxonomy.secondary_industries.map((sec, idx) => (
                        <Badge key={idx} className="bg-blue-500/20 text-blue-300">
                          {sec}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub Industries */}
                {taxonomy.sub_industries && Object.keys(taxonomy.sub_industries).length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Sub-Industrias:</p>
                    <div className="space-y-2">
                      {Object.entries(taxonomy.sub_industries).map(([parent, subs]) => (
                        <div key={parent} className="pl-4 border-l-2 border-slate-700">
                          <p className="text-xs text-slate-500 mb-1">{parent}:</p>
                          <div className="flex flex-wrap gap-1">
                            {subs.map((sub, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs text-purple-300 border-purple-500/30">
                                {sub}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Business Models */}
                {taxonomy.business_models && taxonomy.business_models.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Modelos de Negocio:</p>
                    <div className="flex flex-wrap gap-2">
                      {taxonomy.business_models.map((model, idx) => (
                        <Badge key={idx} className="bg-green-500/20 text-green-300">
                          {model}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="glass-dark border-blue-500/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {editingTaxonomy ? 'Editar Taxonomía' : 'Nueva Taxonomía de Industria'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Primary Industry */}
            <div>
              <Label htmlFor="primary" className="text-slate-300">
                Industria Principal *
              </Label>
              <Input
                id="primary"
                value={formData.primary_industry}
                onChange={(e) => setFormData({ ...formData, primary_industry: e.target.value })}
                className="form-input"
                placeholder="Ej: Retail, Manufacturing, Services"
              />
            </div>

            {/* Secondary Industries */}
            <div>
              <Label className="text-slate-300 mb-2 block">Industrias Secundarias</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={newSecondary}
                  onChange={(e) => setNewSecondary(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSecondary()}
                  className="form-input"
                  placeholder="Ej: Retail B2C, Retail B2B"
                />
                <Button onClick={handleAddSecondary} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.secondary_industries.map((sec, idx) => (
                  <Badge key={idx} className="bg-blue-500/20 text-blue-300 pr-1">
                    {sec}
                    <button
                      onClick={() => handleRemoveSecondary(idx)}
                      className="ml-2 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Sub Industries */}
            {formData.secondary_industries.length > 0 && (
              <div>
                <Label className="text-slate-300 mb-2 block">Sub-Industrias</Label>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <select
                    value={newSubIndustry.parent}
                    onChange={(e) => setNewSubIndustry({ ...newSubIndustry, parent: e.target.value })}
                    className="form-input"
                  >
                    <option value="">Seleccionar padre...</option>
                    {formData.secondary_industries.map((sec, idx) => (
                      <option key={idx} value={sec}>{sec}</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <Input
                      value={newSubIndustry.value}
                      onChange={(e) => setNewSubIndustry({ ...newSubIndustry, value: e.target.value })}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddSubIndustry()}
                      className="form-input"
                      placeholder="Ej: Physical Stores, Ecommerce"
                    />
                    <Button onClick={handleAddSubIndustry} size="sm">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {Object.entries(formData.sub_industries).map(([parent, subs]) => (
                    <div key={parent} className="pl-4 border-l-2 border-slate-700">
                      <p className="text-xs text-slate-500 mb-1">{parent}:</p>
                      <div className="flex flex-wrap gap-1">
                        {subs.map((sub, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs text-purple-300 border-purple-500/30 pr-1">
                            {sub}
                            <button
                              onClick={() => handleRemoveSubIndustry(parent, idx)}
                              className="ml-2 hover:text-red-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Business Models */}
            <div>
              <Label className="text-slate-300 mb-2 block">Modelos de Negocio</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={newBusinessModel}
                  onChange={(e) => setNewBusinessModel(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddBusinessModel()}
                  className="form-input"
                  placeholder="Ej: Multi-location, Franchise, Online-only"
                />
                <Button onClick={handleAddBusinessModel} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.business_models.map((model, idx) => (
                  <Badge key={idx} className="bg-green-500/20 text-green-300 pr-1">
                    {model}
                    <button
                      onClick={() => handleRemoveBusinessModel(idx)}
                      className="ml-2 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
              <Button onClick={handleCloseDialog} variant="ghost">
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saveTaxonomyMutation.isPending}
                className="btn-gradient"
              >
                {saveTaxonomyMutation.isPending ? (
                  <>Guardando...</>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingTaxonomy ? 'Actualizar' : 'Crear'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IndustryTaxonomyManager;
